/**
 * What the app can show of a file, decided from its name alone.
 *
 * It lives outside the preview component because the decision is now made *before* the
 * bytes are asked for. Tapping a 20MB PDF used to pull the whole thing to the phone in
 * order to display "предпросмотр недоступен" — the transfer was the entire cost of the
 * gesture and none of its value. Now the list asks the same question first and only
 * fetches what it can actually draw.
 */
export type FileKind = 'image' | 'text' | 'other';

const IMAGE_EXT = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg']);

const TEXT_EXT = new Set([
  'txt', 'md', 'markdown', 'json', 'js', 'jsx', 'ts', 'tsx', 'css', 'scss', 'html', 'htm',
  'xml', 'yml', 'yaml', 'csv', 'log', 'sh', 'zsh', 'bash', 'py', 'rb', 'go', 'rs', 'c', 'h',
  'cpp', 'java', 'kt', 'swift', 'php', 'sql', 'toml', 'ini', 'conf', 'env', 'gitignore',
]);

export const MIME: Record<string, string> = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif',
  webp: 'image/webp', bmp: 'image/bmp', svg: 'image/svg+xml',
};

/** How big an extension-less file may be before we stop assuming it is text. */
const NAMELESS_TEXT_MAX = 1024 * 1024;

export function ext(name: string): string {
  const i = name.lastIndexOf('.');
  return i < 0 ? '' : name.slice(i + 1).toLowerCase();
}

/**
 * `size` is only consulted for files with no extension at all (README, Dockerfile,
 * .env) — those are usually text, but a 40MB one is not something to render into a
 * `<pre>`. A null size means the Mac could not stat it, which is not a reason to guess
 * generously.
 */
export function fileKind(name: string, size: number | null): FileKind {
  const e = ext(name);
  if (IMAGE_EXT.has(e)) return 'image';
  if (TEXT_EXT.has(e)) return 'text';
  if (e === '' && size !== null && size <= NAMELESS_TEXT_MAX) return 'text';
  return 'other';
}

export function decodeText(b64: string): string {
  try {
    return new TextDecoder('utf-8', { fatal: false }).decode(
      Uint8Array.from(atob(b64), (c) => c.charCodeAt(0)),
    );
  } catch {
    return '';
  }
}

export function humanSize(bytes: number | null): string {
  if (bytes === null) return '';
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
}
